'use client';

import { memo, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { TFunction } from 'i18next';
import {
  AlertTriangle,
  Bookmark,
  BookOpen,
  Building2,
  ChevronDown,
  ChevronRight,
  Euro,
  FileText,
  Gavel,
  Home,
  Lock,
  MapPinHouse,
  RefreshCw,
  Scale,
  ScrollText,
  TrendingDown,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  CompanyHistory,
  InsolvencyRecordItem,
  SubsidiaryCompanyItem,
  TenderItem,
} from '@/lib/backend-api';
import {
  foundingDateAtPrecision,
  generateCompanySlug,
  tryBuildCompanySlugFromCanonical,
} from '@/lib/company-utils';
import type { Locale } from '@/lib/i18n-routes';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LocaleLink } from '@/components/locale-link';
import { SourceAttribution } from './SourceAttribution';

// =============================================================================
// TYPES
// =============================================================================

interface CompanyEvent {
  timestamp: number;
  date: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  title: string;
  value?: string;
  detail?: string;
  alwaysExpanded?: boolean;
}

interface CapitalEvent {
  timestamp: number;
  date: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  type: string;
  delta?: string;
  deltaColor?: string;
  total: string;
}

interface BoardEvent {
  timestamp: number;
  date: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  name: string;
  role?: string;
  age?: number | null;
  status: string;
  statusColor?: string;
}

interface InsolvencyEvent {
  timestamp: number;
  date: string;
  category: string;
  aktenzeichen: string;
  court: string;
  dissolved: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

function parseDDMMYYYY(
  dateStr?: string,
): { year: number; month: number; day: number } | null {
  if (!dateStr) return null;
  const parts = dateStr.split('.');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (!isNaN(day) && !isNaN(month) && !isNaN(year))
      return { year, month, day };
  }
  const iso = new Date(dateStr);
  if (!isNaN(iso.getTime())) {
    return {
      year: iso.getFullYear(),
      month: iso.getMonth() + 1,
      day: iso.getDate(),
    };
  }
  return null;
}

function toTimestamp(dateStr?: string): number | null {
  const p = parseDDMMYYYY(dateStr);
  if (!p) return null;
  return new Date(p.year, p.month - 1, p.day).getTime();
}

function formatDateDE(dateStr?: string): string {
  const parsed = parseDDMMYYYY(dateStr);
  if (!parsed) return '—';
  return `${String(parsed.day).padStart(2, '0')}.${String(parsed.month).padStart(2, '0')}.${parsed.year}`;
}

/** Founding-type date (first entry, founding document): the bare year when
 *  the register only knows the year (its 1 January placeholder), else null. */
function foundingYearOnly(dateStr?: string): string | null {
  const precise = foundingDateAtPrecision(dateStr);
  return precise && /^\d{4}$/.test(precise) ? precise : null;
}

/** TED publishes ISO dates, unlike the CD timeline's DD.MM.YYYY. */
function formatDateISO(dateStr?: string | null): string {
  if (!dateStr) return '—';
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString('de-DE');
}

function formatCurrency(amount: number, currency?: string): string {
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('de-DE')} ${currency || 'EUR'}`;
  }
}

function memberDisplayName(member: Record<string, any>): string {
  // Prefer new granular fields: [title] [first_name] [last_name]
  if (member.first_name || member.last_name) {
    const parts = [member.title, member.first_name, member.last_name].filter(
      Boolean,
    );
    if (parts.length > 0) return parts.join(' ');
  }
  // Fall back to full_name
  if (member.full_name) return member.full_name;
  // Fall back to legacy name/surname
  const surname = member.surname || member.last_name;
  const firstName = member.name || member.first_name || member.vorname;
  const titles =
    Array.isArray(member.titles) && member.titles.length > 0
      ? member.titles.join(' ')
      : '';
  const namePart =
    surname && firstName
      ? `${surname}, ${firstName}`
      : surname || firstName || '';
  if (namePart && titles) return `${titles} ${namePart}`;
  if (namePart) return namePart;
  if (member.organization) return member.organization;
  return 'Unknown';
}

function calculateAge(dateStr: string): number | null {
  // Try DD.MM.YYYY
  const p = parseDDMMYYYY(dateStr);
  if (p) {
    const today = new Date();
    let age = today.getFullYear() - p.year;
    if (
      today.getMonth() + 1 < p.month ||
      (today.getMonth() + 1 === p.month && today.getDate() < p.day)
    )
      age--;
    return age >= 0 ? age : null;
  }
  // Try ISO
  try {
    const dob = new Date(dateStr);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    if (
      today.getMonth() < dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
    )
      age--;
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
}

// =============================================================================
// EVENT BUILDERS
// =============================================================================

function buildCompanyEvents(
  history: CompanyHistory,
  t: TFunction,
): CompanyEvent[] {
  const events: CompanyEvent[] = [];

  const processList = (
    items: CompanyHistory['firma'],
    titleFirst: string,
    titleChange: string,
    icon: LucideIcon,
    iconBg: string,
    iconColor: string,
    getValue?: (data: Record<string, any>) => string | undefined,
    getDetail?: (data: Record<string, any>) => string | undefined,
    alwaysExpanded?: boolean,
  ) => {
    const sorted = [...items].sort(
      (a, b) =>
        (toTimestamp(a.entry_date ?? a.data.entry_date) ?? 0) -
        (toTimestamp(b.entry_date ?? b.data.entry_date) ?? 0),
    );
    for (let i = 0; i < sorted.length; i++) {
      const evt = sorted[i];
      const dateStr = evt.entry_date ?? evt.data.entry_date;
      const ts = toTimestamp(dateStr);
      if (ts == null) continue;
      events.push({
        timestamp: ts,
        date: formatDateDE(dateStr),
        icon,
        iconBg,
        iconColor,
        title: i === 0 ? titleFirst : titleChange,
        value: getValue?.(evt.data),
        detail: getDetail?.(evt.data),
        alwaysExpanded,
      });
    }
  };

  processList(
    history.firma,
    t('companyHistory.nameRegistered'),
    t('companyHistory.nameChange'),
    Building2,
    'bg-violet-100 dark:bg-violet-900/40',
    'text-violet-600 dark:text-violet-400',
    (d) => d.firma,
  );
  processList(
    history.sitz,
    t('companyHistory.seatRegistered'),
    t('companyHistory.seatChange'),
    Home,
    'bg-emerald-100 dark:bg-emerald-900/40',
    'text-emerald-600 dark:text-emerald-400',
    (d) => d.sitz,
  );
  processList(
    history.geschaeftsanschrift,
    t('companyHistory.addressRegistered'),
    t('companyHistory.addressChange'),
    MapPinHouse,
    'bg-teal-100 dark:bg-teal-900/40',
    'text-teal-600 dark:text-teal-400',
    (d) => d.geschaeftsanschrift,
  );
  processList(
    history.rechtsform,
    t('companyHistory.legalFormRegistered'),
    t('companyHistory.legalFormChange'),
    Scale,
    'bg-purple-100 dark:bg-purple-900/40',
    'text-purple-600 dark:text-purple-400',
    (d) => d.rechtsform,
    (d) =>
      d.previous_rechtsform
        ? `${d.previous_rechtsform} → ${d.rechtsform}`
        : undefined,
  );
  processList(
    history.gegenstand,
    t('companyHistory.purposeRegistered'),
    t('companyHistory.purposeChange'),
    ScrollText,
    'bg-orange-100 dark:bg-orange-900/40',
    'text-orange-600 dark:text-orange-400',
    undefined,
    (d) => d.gegenstand,
    true,
  );

  // Satzung / Gesellschaftsvertrag events
  // Skip the first satzung event (entry_number 1) when erste_eintragung already
  // shows the Gründungsdokument info, as they are redundant.
  const skipFirstSatzung =
    history.erste_eintragung?.data?.gruendungsdokument &&
    history.erste_eintragung?.data?.gruendungsdokument_datum;
  if (history.satzung_events?.length) {
    for (const evt of history.satzung_events) {
      if (skipFirstSatzung && evt.entry_number === 1) continue;
      const dateStr =
        evt.entry_date ?? evt.data.entry_date ?? evt.data.document_date;
      const ts = toTimestamp(dateStr);
      if (ts == null) continue;
      const isCreated = evt.data.event_type === 'created';
      const docType =
        evt.data.document_type || t('companyHistory.satzung', 'Satzung');
      events.push({
        timestamp: ts,
        date: formatDateDE(dateStr),
        icon: BookOpen,
        iconBg: 'bg-indigo-100 dark:bg-indigo-900/40',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        title: isCreated
          ? t('companyHistory.satzungCreated', '{{docType}} erstellt', {
              docType,
            })
          : t('companyHistory.satzungAmended', '{{docType}} geändert', {
              docType,
            }),
        value: docType,
      });
    }
  }

  // Erste Eintragung (first registration)
  if (history.erste_eintragung) {
    const evt = history.erste_eintragung;
    const dateStr =
      evt.entry_date ?? evt.data.entry_date ?? evt.data.erste_eintragung_datum;
    const ts = toTimestamp(dateStr);
    if (ts != null) {
      const detail = evt.data.has_earlier_entries
        ? t(
            'companyHistory.migratedFromOtherRegister',
            'Umschreibung aus vorherigem Registerblatt',
          )
        : undefined;
      const docYearOnly = foundingYearOnly(evt.data.gruendungsdokument_datum);
      const docInfo =
        evt.data.gruendungsdokument && evt.data.gruendungsdokument_datum
          ? docYearOnly
            ? `${evt.data.gruendungsdokument} von ${docYearOnly}`
            : `${evt.data.gruendungsdokument} vom ${evt.data.gruendungsdokument_datum}`
          : evt.data.gruendungsdokument || undefined;
      events.push({
        timestamp: ts,
        date: foundingYearOnly(dateStr) ?? formatDateDE(dateStr),
        icon: Bookmark,
        iconBg: 'bg-sky-100 dark:bg-sky-900/40',
        iconColor: 'text-sky-600 dark:text-sky-400',
        title: t('companyHistory.firstRegistration', 'Erste Eintragung'),
        value: docInfo,
        detail,
      });
    }
  }

  return events.sort((a, b) => b.timestamp - a.timestamp);
}

function buildCapitalEvents(
  history: CompanyHistory,
  t: TFunction,
): CapitalEvent[] {
  const events: CapitalEvent[] = [];

  for (const evt of history.kapital) {
    const dateStr = evt.entry_date ?? evt.data.entry_date;
    const ts = toTimestamp(dateStr);
    if (ts == null) continue;
    const delta = evt.data.delta;
    const isIncrease = delta != null && delta > 0;
    const isDecrease = delta != null && delta < 0;

    events.push({
      timestamp: ts,
      date: formatDateDE(dateStr),
      icon: isIncrease ? TrendingUp : isDecrease ? TrendingDown : Euro,
      iconBg: isIncrease
        ? 'bg-green-100 dark:bg-green-900/40'
        : isDecrease
          ? 'bg-red-100 dark:bg-red-900/40'
          : 'bg-amber-100 dark:bg-amber-900/40',
      iconColor: isIncrease
        ? 'text-green-600 dark:text-green-400'
        : isDecrease
          ? 'text-red-600 dark:text-red-400'
          : 'text-amber-600 dark:text-amber-400',
      type: isIncrease
        ? t('companyHistory.capitalIncrease', 'Capital Increase')
        : isDecrease
          ? t('companyHistory.capitalDecrease', 'Capital Decrease')
          : t('companyHistory.capitalSet', 'Share Capital Set'),
      delta:
        delta != null && delta !== 0
          ? `${delta > 0 ? '+' : ''}${formatCurrency(delta, evt.data.currency)}`
          : undefined,
      deltaColor: isIncrease
        ? 'text-green-600 dark:text-green-400'
        : isDecrease
          ? 'text-red-600 dark:text-red-400'
          : undefined,
      total: formatCurrency(evt.data.amount, evt.data.currency),
    });
  }

  return events.sort((a, b) => b.timestamp - a.timestamp);
}

function buildBoardEvents(history: CompanyHistory, t: TFunction): BoardEvent[] {
  const events: BoardEvent[] = [];

  for (const evt of history.board_members) {
    const dateStr = evt.entry_date ?? evt.data.entry_date;
    const ts = toTimestamp(dateStr);
    if (ts == null) continue;
    const member = evt.data.member || {};
    const action = evt.data.action || 'entered';
    const isJoined = action === 'entered';
    const isLeft = action === 'exited';
    const position = member.position || member.rolle || '';

    // Kommanditisten are folded into board_members by the CD aggregator
    // (position "Kommanditist"), but they are limited partners, not management
    // — keep them out of the Geschäftsführung timeline. A large family KG can
    // otherwise flood it with hundreds of Kommanditist join/leave events.
    if (/kommanditist/i.test(position)) continue;

    // Calculate age
    const birthStr = member.birthdate || member.date_of_birth;
    const age = birthStr ? calculateAge(birthStr) : null;

    events.push({
      timestamp: ts,
      date: formatDateDE(dateStr),
      icon: isJoined ? UserPlus : isLeft ? UserMinus : RefreshCw,
      iconBg: isJoined
        ? 'bg-green-100 dark:bg-green-900/40'
        : isLeft
          ? 'bg-red-100 dark:bg-red-900/40'
          : 'bg-slate-100 dark:bg-slate-900/40',
      iconColor: isJoined
        ? 'text-green-600 dark:text-green-400'
        : isLeft
          ? 'text-red-600 dark:text-red-400'
          : 'text-slate-600 dark:text-slate-400',
      name: memberDisplayName(member),
      role: position || undefined,
      age,
      status: isJoined
        ? t('companyHistory.joined', 'Joined')
        : isLeft
          ? t('companyHistory.left', 'Left')
          : t('companyHistory.changed', 'Changed'),
      statusColor: isJoined
        ? 'text-green-600 dark:text-green-400'
        : isLeft
          ? 'text-red-600 dark:text-red-400'
          : 'text-muted-foreground',
    });
  }

  return events.sort((a, b) => b.timestamp - a.timestamp);
}

function buildInsolvencyEvents(
  records: InsolvencyRecordItem[],
): InsolvencyEvent[] {
  const events: InsolvencyEvent[] = [];
  for (const r of records) {
    // Undated events are kept rather than dropped: a plain "Die Gesellschaft
    // ist aufgelöst." states no date, and it is exactly the entry that puts a
    // company into liquidation. Sorting them last beats hiding them.
    const ts = toTimestamp(r.publish_date);
    events.push({
      timestamp: ts ?? 0,
      date: ts == null ? '—' : formatDateDE(r.publish_date),
      category: r.category,
      aktenzeichen: r.aktenzeichen || '—',
      court: r.court || '—',
      dissolved: r.dissolved === true,
    });
  }
  return events.sort((a, b) => b.timestamp - a.timestamp);
}

// =============================================================================
// TABLE RENDERERS
// =============================================================================

const headerCls =
  'relative text-left align-middle text-xs font-normal text-accent-foreground px-2.5 h-8 border-b border-border first:pl-5 last:pr-5';
const cellCls = 'text-xs py-1.5 px-2 align-top first:pl-5 last:pr-5';

function EventIcon({
  icon: Icon,
  iconBg,
  iconColor,
}: {
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-md',
        iconBg,
      )}
    >
      <Icon size={11} className={iconColor} />
    </div>
  );
}

function CompanyEventsTable({
  events,
  t,
  blurAfter,
}: {
  events: CompanyEvent[];
  t: TFunction;
  blurAfter?: number;
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[85px]" />
          <col />
          <col />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>{t('companyHistory.colEventType')}</th>
            <th className={headerCls}>{t('companyHistory.colEvent')}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => {
            const isExpanded = evt.alwaysExpanded || expandedIdx === idx;
            const isCollapsible = !!evt.detail && !evt.alwaysExpanded;
            const isBlurred = blurAfter != null && idx >= blurAfter;
            return (
              <tr
                key={idx}
                className={cn(
                  'border-b border-border/40 transition-colors',
                  isCollapsible && !isBlurred && 'cursor-pointer',
                  isBlurred && 'select-none',
                  isExpanded && !evt.alwaysExpanded
                    ? 'bg-muted/50'
                    : 'hover:bg-muted/30',
                )}
                onClick={() =>
                  !isBlurred &&
                  isCollapsible &&
                  setExpandedIdx(isExpanded ? null : idx)
                }
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.date}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={evt.icon}
                      iconBg={evt.iconBg}
                      iconColor={evt.iconColor}
                    />
                    <span className="break-words min-w-0">{evt.title}</span>
                    {isCollapsible &&
                      !isBlurred &&
                      (isExpanded ? (
                        <ChevronDown
                          size={11}
                          className="text-muted-foreground/40 shrink-0 mt-0.5"
                        />
                      ) : (
                        <ChevronRight
                          size={11}
                          className="text-muted-foreground/40 shrink-0 mt-0.5"
                        />
                      ))}
                  </div>
                </td>
                <td className={cn(cellCls, isBlurred && 'blur-[5px]')}>
                  {evt.value && (
                    <span className="text-foreground break-words">
                      {evt.value}
                    </span>
                  )}
                  {evt.detail && (
                    <p
                      className={cn(
                        evt.value
                          ? 'text-muted-foreground/80 mt-0.5'
                          : 'text-foreground',
                        isExpanded
                          ? 'whitespace-pre-wrap break-words'
                          : 'line-clamp-2 break-words',
                      )}
                    >
                      {evt.detail}
                    </p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CapitalEventsTable({
  events,
  t,
  blurAfter,
}: {
  events: CapitalEvent[];
  t: TFunction;
  blurAfter?: number;
}) {
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[85px]" />
          <col />
          <col className="w-[90px] sm:w-auto" />
          <col className="w-[90px] sm:w-auto" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>{t('companyHistory.colType')}</th>
            <th className={cn(headerCls, 'text-right')}>
              {t('companyHistory.colChange')}
            </th>
            <th className={cn(headerCls, 'text-right')}>
              {t('companyHistory.colTotal')}
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => {
            const isBlurred = blurAfter != null && idx >= blurAfter;
            return (
              <tr
                key={idx}
                className={cn(
                  'border-b border-border/40 hover:bg-muted/30 transition-colors',
                  isBlurred && 'select-none',
                )}
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.date}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={evt.icon}
                      iconBg={evt.iconBg}
                      iconColor={evt.iconColor}
                    />
                    <span className="break-words min-w-0">{evt.type}</span>
                  </div>
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-right font-semibold break-words',
                    evt.deltaColor || 'text-muted-foreground',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.delta || '—'}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-right text-foreground font-medium break-words',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.total}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BoardEventsTable({
  events,
  t,
  blurAfter,
  isAuthenticated = true,
}: {
  events: BoardEvent[];
  t: TFunction;
  blurAfter?: number;
  isAuthenticated?: boolean;
}) {
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[85px]" />
          <col />
          <col className="hidden sm:table-column" />
          {isAuthenticated && (
            <col className="hidden sm:table-column w-[50px]" />
          )}
          <col className="w-[140px] sm:w-auto" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>{t('companyHistory.colName')}</th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colRole')}
            </th>
            {isAuthenticated && (
              <th className={cn(headerCls, 'hidden sm:table-cell')}>
                {t('companyHistory.colAge')}
              </th>
            )}
            <th className={headerCls}>{t('companyHistory.colStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => {
            const isBlurred = blurAfter != null && idx >= blurAfter;
            return (
              <tr
                key={idx}
                className={cn(
                  'border-b border-border/40 hover:bg-muted/30 transition-colors',
                  isBlurred && 'select-none',
                )}
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.date}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground break-words',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <span>{evt.name}</span>
                  {evt.role && (
                    <span className="sm:hidden text-muted-foreground text-[10px] block">
                      {evt.role}
                    </span>
                  )}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-foreground hidden sm:table-cell',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.role || '—'}
                </td>
                {isAuthenticated && (
                  <td
                    className={cn(
                      cellCls,
                      'text-muted-foreground hidden sm:table-cell',
                      isBlurred && 'blur-[5px]',
                    )}
                  >
                    {evt.age ?? '—'}
                  </td>
                )}
                <td
                  className={cn(
                    cellCls,
                    'font-medium',
                    evt.statusColor,
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={evt.icon}
                      iconBg={evt.iconBg}
                      iconColor={evt.iconColor}
                    />
                    <span className="break-words min-w-0">{evt.status}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SubsidiariesTable({
  subsidiaries,
  t,
  blurAfter,
}: {
  subsidiaries: SubsidiaryCompanyItem[];
  t: TFunction;
  blurAfter?: number;
}) {
  const { locale } = useLocale();
  if (subsidiaries.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-auto">
      <table className="w-full table-fixed">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={headerCls}>
              {t('companyDetail.participationCompany', 'Company')}
            </th>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyDetail.participationLocation', 'Location')}
            </th>
          </tr>
        </thead>
        <tbody>
          {subsidiaries.map((sub, idx) => {
            const isBlurred = blurAfter != null && idx >= blurAfter;
            const hasLink = sub.eu_id && sub.register_court;
            const iconUrl =
              getPublicStorageUrl(sub.logo_url) ||
              getPublicStorageUrl(sub.favicon_url);
            return (
              <tr
                key={sub.eu_id || idx}
                className={cn(
                  'border-b border-border/40 hover:bg-muted/30 transition-colors',
                  isBlurred && 'select-none',
                )}
              >
                <td className={cn(cellCls, isBlurred && 'blur-[5px]')}>
                  <div className="flex items-center gap-1.5">
                    {iconUrl ? (
                      <img
                        src={iconUrl}
                        alt={`${sub.name} Logo`}
                        className="h-3.5 w-3.5 shrink-0 rounded-sm object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    )}
                    {hasLink && !isBlurred ? (
                      <LocaleLink
                        href={generateCompanySlug(
                          {
                            name: sub.name,
                            registerCourt: sub.register_court!,
                            registerType: sub.register_type ?? undefined,
                            registerId: sub.register_id ?? undefined,
                          },
                          locale,
                        )}
                        className="text-sm hover:underline"
                      >
                        {sub.name}
                      </LocaleLink>
                    ) : (
                      <span className="text-sm">{sub.name}</span>
                    )}
                  </div>
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {sub.sitz || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function InsolvencyEventsTable({
  events,
  t,
}: {
  events: InsolvencyEvent[];
  t: TFunction;
}) {
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[17%] sm:w-[14%]" />
          <col className="w-[63%] sm:w-[32%]" />
          <col className="hidden sm:table-column sm:w-[22%]" />
          <col className="hidden sm:table-column sm:w-[22%]" />
          <col className="w-[20%] sm:w-[10%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>
              {t('companyHistory.colCategory', 'Kategorie')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colAktenzeichen', 'Aktenzeichen')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colCourt', 'Gericht / Amt')}
            </th>
            <th className={cn(headerCls, 'text-center')}>
              {t('companyHistory.colDissolved', 'Aufgelöst')}
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => (
            <tr
              key={idx}
              className="border-b border-border/40 hover:bg-muted/30 transition-colors"
            >
              <td
                className={cn(
                  cellCls,
                  'text-muted-foreground whitespace-nowrap tabular-nums',
                )}
              >
                {evt.date}
              </td>
              <td className={cn(cellCls, 'font-medium text-foreground')}>
                <div className="flex items-start gap-1.5">
                  <EventIcon
                    icon={Gavel}
                    iconBg="bg-orange-100 dark:bg-orange-900/40"
                    iconColor="text-orange-600 dark:text-orange-400"
                  />
                  <span className="break-words min-w-0">
                    {t(`companyHistory.insolvencyCategory.${evt.category}`, {
                      defaultValue: evt.category,
                    })}
                  </span>
                </div>
                <div className="sm:hidden text-muted-foreground text-[10px] mt-0.5 pl-[26px]">
                  {evt.aktenzeichen} · {evt.court}
                </div>
              </td>
              <td
                className={cn(
                  cellCls,
                  'text-foreground hidden sm:table-cell tabular-nums break-words',
                )}
              >
                {evt.aktenzeichen}
              </td>
              <td
                className={cn(
                  cellCls,
                  'text-foreground hidden sm:table-cell break-words',
                )}
              >
                {evt.court}
              </td>
              <td className={cn(cellCls, 'text-center')}>
                {evt.dissolved ? (
                  <span className="inline-flex items-center rounded-full bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
                    {t('companyHistory.dissolvedYes', 'Ja')}
                  </span>
                ) : (
                  <span className="text-muted-foreground/60">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * EU public-procurement notices this company appears in.
 *
 * Lives here rather than in a card of its own because tenders are dated
 * events like every other tab, and because the card that used to hold them
 * rendered its own header row in hardcoded English.
 *
 * `role` is per notice, not per company: the same company can be the buyer of
 * one and the winner of another, so a row states which side it was on and
 * links the counterparty.
 */
function TendersTable({
  tenders,
  t,
  locale,
  blurAfter,
}: {
  tenders: TenderItem[];
  t: TFunction;
  locale: string;
  blurAfter?: number;
}) {
  if (tenders.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[17%] sm:w-[13%]" />
          <col className="w-[58%] sm:w-[35%]" />
          <col className="hidden sm:table-column sm:w-[13%]" />
          <col className="hidden sm:table-column sm:w-[21%]" />
          <col className="w-[25%] sm:w-[18%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>
              {t('companyHistory.colTenderTitle', 'Auftrag')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colTenderRole', 'Rolle')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colTenderCounterpart', 'Gegenseite')}
            </th>
            <th className={cn(headerCls, 'text-right')}>
              {t('companyHistory.colTenderValue', 'Wert')}
            </th>
          </tr>
        </thead>
        <tbody>
          {tenders.map((tender, idx) => {
            const isBlurred = blurAfter != null && idx >= blurAfter;
            // Awarded value where the contract is decided, estimate while it
            // is still open. Both are filtered server-side for placeholder
            // amounts, so a missing figure means "not published".
            const value = tender.awarded_value ?? tender.estimated_value;
            const currency =
              tender.awarded_currency ?? tender.value_currency ?? 'EUR';
            const counterpartSlug = tender.counterpart_name
              ? tryBuildCompanySlugFromCanonical(
                  {
                    canonical_name: tender.counterpart_canonical_name,
                    canonical_sitz: tender.counterpart_canonical_sitz,
                    canonical_register_court:
                      tender.counterpart_canonical_register_court,
                    canonical_register_type:
                      tender.counterpart_canonical_register_type,
                    canonical_register_id:
                      tender.counterpart_canonical_register_id,
                  },
                  locale as Locale,
                )
              : null;
            return (
              <tr
                key={`${tender.publication_number}-${tender.role}`}
                className="border-b border-border/40 hover:bg-muted/30 transition-colors"
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  {formatDateISO(tender.publication_date)}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={Gavel}
                      iconBg="bg-sky-100 dark:bg-sky-900/40"
                      iconColor="text-sky-600 dark:text-sky-400"
                    />
                    <span className="break-words min-w-0">
                      {isBlurred || !tender.publication_number ? (
                        (tender.title ?? '—')
                      ) : (
                        <a
                          href={`https://ted.europa.eu/de/notice/-/detail/${tender.publication_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {tender.title ?? '—'}
                        </a>
                      )}
                    </span>
                  </div>
                  {/* The role/counterpart columns are hidden below sm, so
                      restate them here or a phone shows a date and a title
                      and no way to tell which side the company was on. */}
                  <div className="sm:hidden text-muted-foreground text-[10px] mt-0.5 pl-[26px]">
                    {t(`companyHistory.tenderRole.${tender.role}`, {
                      defaultValue: tender.role,
                    })}
                    {tender.counterpart_name ? ` · ${tender.counterpart_name}` : ''}
                  </div>
                </td>
                <td
                  className={cn(
                    cellCls,
                    'hidden sm:table-cell',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  <Badge
                    variant={tender.role === 'winner' ? 'success' : 'secondary'}
                    appearance="ghost"
                    className="text-[10px]"
                  >
                    {t(`companyHistory.tenderRole.${tender.role}`, {
                      defaultValue: tender.role,
                    })}
                  </Badge>
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-foreground hidden sm:table-cell break-words',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  {!tender.counterpart_name ? (
                    <span className="text-muted-foreground/60">—</span>
                  ) : counterpartSlug && !isBlurred ? (
                    <LocaleLink
                      href={counterpartSlug}
                      className="text-primary hover:underline"
                    >
                      {tender.counterpart_name}
                    </LocaleLink>
                  ) : (
                    tender.counterpart_name
                  )}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-right tabular-nums whitespace-nowrap text-foreground',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  {value != null ? (
                    formatCurrency(value, currency)
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const PREVIEW_ROWS = 2;

export const CompanyHistoryCard = memo(function CompanyHistoryCard({
  history,
  isAuthenticated = true,
  subsidiaries = [],
  insolvencyRecords = [],
  tenders = [],
  cpvProducts = [],
  tendersTotal = 0,
  euId,
  showRegisterAttribution = false,
  registerCourt,
  registerId,
  capturedAt,
  className,
}: {
  /**
   * The CD-derived timeline, or `null` when the company has no CD event
   * aggregate — which is 38% of the companies that have tenders. The card
   * still mounts for those: the Vergabe tab is the only content it has, and
   * hiding it would lose the data entirely.
   */
  history: CompanyHistory | null;
  isAuthenticated?: boolean;
  euId?: string;
  subsidiaries?: SubsidiaryCompanyItem[];
  insolvencyRecords?: InsolvencyRecordItem[];
  /** TED notices, newest first, already deduplicated server-side. */
  tenders?: TenderItem[];
  /** CPV category descriptions across those notices — what the company buys or sells. */
  cpvProducts?: string[];
  /**
   * Notices this company appears in, counted before the server's page limit
   * and its per-role dedup. `tenders.length` is a different number and must
   * not be shown in its place.
   */
  tendersTotal?: number;
  showRegisterAttribution?: boolean;
  registerCourt?: string | null;
  registerId?: string | null;
  capturedAt?: string | null;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { locale } = useLocale();

  const companyEvents = useMemo(
    () => (history ? buildCompanyEvents(history, t) : []),
    [history, t],
  );
  const capitalEvents = useMemo(
    () => (history ? buildCapitalEvents(history, t) : []),
    [history, t],
  );
  const boardEvents = useMemo(() => {
    const events = history ? buildBoardEvents(history, t) : [];
    if (!isAuthenticated) return events.map((e) => ({ ...e, age: null }));
    return events;
  }, [history, t, isAuthenticated]);
  const insolvencyEvents = useMemo(
    () => buildInsolvencyEvents(insolvencyRecords),
    [insolvencyRecords],
  );

  const hasInsolvencyTab = insolvencyEvents.length > 0;
  const hasTendersTab = tenders.length > 0;
  const tabCount = 3 + (hasInsolvencyTab ? 1 : 0) + (hasTendersTab ? 1 : 0);

  const defaultTab =
    companyEvents.length > 0
      ? 'company'
      : capitalEvents.length > 0
        ? 'capital'
        : isAuthenticated && boardEvents.length > 0
          ? 'board'
          : hasTendersTab
            ? 'tenders'
            : 'company';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const totalEvents =
    companyEvents.length +
    capitalEvents.length +
    (isAuthenticated ? boardEvents.length : 0) +
    (isAuthenticated && hasInsolvencyTab ? insolvencyEvents.length : 0) +
    // Guests see a blurred preview rather than nothing, so tenders count
    // toward "is there anything here" for them too — unlike the board tab,
    // which is replaced by a lock.
    tendersTotal;
  if (totalEvents === 0 && (!boardEvents.length || isAuthenticated))
    return null;

  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <div className="flex items-start gap-2 w-full">
          <div className="flex-1">
            <CardTitle as="h2">
              {t('companyDetail.companyHistory', 'Unternehmenshistorie')}
            </CardTitle>
          </div>
          <Badge variant="outline" className="shrink-0 mt-1">
            {totalEvents}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-5 pt-2">
            <TabsList
              variant="line"
              size="sm"
              className={cn(
                'w-full grid grid-cols-2',
                tabCount === 5
                  ? 'sm:grid-cols-5'
                  : tabCount === 4
                    ? 'sm:grid-cols-4'
                    : 'sm:grid-cols-3',
                !isAuthenticated ? 'mb-1' : 'mb-3',
              )}
            >
              <TabsTrigger
                value="company"
                className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                disabled={companyEvents.length === 0}
              >
                <FileText size={13} className="shrink-0" />
                <span className="truncate">
                  {t('companyHistory.tabCompany', 'Company')}
                </span>
                {companyEvents.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {companyEvents.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="capital"
                className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                disabled={capitalEvents.length === 0}
              >
                <Euro size={13} className="shrink-0" />
                <span className="truncate">
                  {t('companyHistory.tabCapital', 'Capital')}
                </span>
                {capitalEvents.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {capitalEvents.length}
                  </Badge>
                )}
              </TabsTrigger>
              {isAuthenticated ? (
                <TabsTrigger
                  value="board"
                  className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                  disabled={boardEvents.length === 0}
                >
                  <Users size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyHistory.tabBoard', 'Management')}
                  </span>
                  {boardEvents.length > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                    >
                      {boardEvents.length}
                    </Badge>
                  )}
                </TabsTrigger>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground cursor-not-allowed px-2 py-1.5 min-h-[44px] sm:min-h-0">
                        <Lock size={12} className="shrink-0" />
                        <span className="truncate">
                          {t('companyHistory.tabBoard', 'Management')}
                        </span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(
                        'companyHistory.loginToSeeBoard',
                        'Kostenlos anmelden um Geschäftsführung zu sehen',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {hasInsolvencyTab &&
                (isAuthenticated ? (
                  <TabsTrigger
                    value="insolvency"
                    className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                  >
                    <AlertTriangle size={13} className="shrink-0" />
                    <span className="truncate">
                      {t('companyHistory.tabInsolvency', 'Insolvenz & Liquidation')}
                    </span>
                    <Badge
                      variant="secondary"
                      className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                    >
                      {insolvencyEvents.length}
                    </Badge>
                  </TabsTrigger>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground cursor-not-allowed px-2 py-1.5 min-h-[44px] sm:min-h-0">
                          <Lock size={12} className="shrink-0" />
                          <span className="truncate">
                            {t('companyHistory.tabInsolvency', 'Insolvenz & Liquidation')}
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(
                          'companyHistory.loginToSeeInsolvency',
                          'Kostenlos anmelden um Insolvenz- und Liquidationsverfahren zu sehen',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              {/* Vergabe is shown to guests as a blurred preview rather than
                  a lock: unlike board members, the first row is already
                  public on TED, so a teaser is honest and converts better
                  than a padlock. */}
              {hasTendersTab && (
                <TabsTrigger
                  value="tenders"
                  className="gap-1 text-[10px] sm:text-xs justify-start min-w-0 min-h-[44px] sm:min-h-0"
                >
                  <Gavel size={13} className="shrink-0" />
                  <span className="truncate">
                    {t('companyHistory.tabTenders', 'Vergabe')}
                  </span>
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-4 px-1 text-[9px] shrink-0"
                  >
                    {tendersTotal || tenders.length}
                  </Badge>
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <div>
            {/* forceMount keeps every panel in the SSR HTML so the register /
                capital event tables are crawlable even when another tab is
                active. Radix doesn't hide force-mounted panels itself, so
                inactive ones are hidden via data-[state=inactive]:hidden. */}
            <TabsContent
              value="company"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <CompanyEventsTable
                events={
                  !isAuthenticated ? companyEvents.slice(0, 3) : companyEvents
                }
                t={t}
                blurAfter={!isAuthenticated ? PREVIEW_ROWS : undefined}
              />
              {!isAuthenticated && companyEvents.length > 2 && (
                <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      trackSignupAndRedirect(pathname, 'company_history', euId);
                    }}
                    className="text-primary hover:underline"
                  >
                    {t(
                      'companyHistory.registerToSeeAll',
                      'Sign in to see all {{count}} events',
                      { count: companyEvents.length },
                    )}
                  </a>
                </div>
              )}
            </TabsContent>
            <TabsContent
              value="capital"
              forceMount
              className="mt-0 data-[state=inactive]:hidden"
            >
              <CapitalEventsTable
                events={
                  !isAuthenticated ? capitalEvents.slice(0, 3) : capitalEvents
                }
                t={t}
                blurAfter={!isAuthenticated ? PREVIEW_ROWS : undefined}
              />
              {!isAuthenticated && capitalEvents.length > 2 && (
                <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      trackSignupAndRedirect(pathname, 'company_history', euId);
                    }}
                    className="text-primary hover:underline"
                  >
                    {t(
                      'companyHistory.registerToSeeAllCapital',
                      'Sign in to see all {{count}} capital events',
                      { count: capitalEvents.length },
                    )}
                  </a>
                </div>
              )}
            </TabsContent>
            {isAuthenticated && (
              <TabsContent
                value="board"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <BoardEventsTable
                  events={boardEvents}
                  t={t}
                  isAuthenticated={isAuthenticated}
                />
              </TabsContent>
            )}
            {isAuthenticated && hasInsolvencyTab && (
              <TabsContent
                value="insolvency"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <InsolvencyEventsTable events={insolvencyEvents} t={t} />
              </TabsContent>
            )}
            {hasTendersTab && (
              <TabsContent
                value="tenders"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                {cpvProducts.length > 0 && (
                  <div className="px-5 py-3 border-b border-border/40">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      {t('companyHistory.cpvCategories', 'CPV-Kategorien')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cpvProducts.slice(0, 12).map((p) => (
                        <Badge
                          key={p}
                          variant="secondary"
                          className="text-[10px]"
                        >
                          {p}
                        </Badge>
                      ))}
                      {cpvProducts.length > 12 && (
                        <Badge variant="outline" className="text-[10px]">
                          +{cpvProducts.length - 12}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                <TendersTable
                  tenders={!isAuthenticated ? tenders.slice(0, 3) : tenders}
                  t={t}
                  locale={locale}
                  blurAfter={!isAuthenticated ? PREVIEW_ROWS : undefined}
                />
                {!isAuthenticated && tendersTotal > PREVIEW_ROWS && (
                  <div
                    className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center"
                    data-nosnippet
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        trackSignupAndRedirect(pathname, 'company_tenders', euId);
                      }}
                      className="text-primary hover:underline"
                    >
                      {t(
                        'companyHistory.registerToSeeAllTenders',
                        'Sign in to see all {{count}} tenders',
                        { count: tendersTotal },
                      )}
                    </a>
                  </div>
                )}
                {/* The server pages tenders; say so rather than letting the
                    badge's count and the row count silently disagree. */}
                {isAuthenticated && tendersTotal > tenders.length && (
                  <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
                    {t(
                      'companyHistory.tendersTruncated',
                      'Showing the {{shown}} most recent of {{total}} notices',
                      { shown: tenders.length, total: tendersTotal },
                    )}
                  </div>
                )}
              </TabsContent>
            )}
          </div>
        </Tabs>
        {showRegisterAttribution && history && (
          <SourceAttribution
            source="handelsregister"
            registerCourt={registerCourt}
            registerId={registerId}
            capturedAt={capturedAt}
            className="mx-5"
          />
        )}
      </CardContent>
    </Card>
  );
});
